import { Link } from "react-router-dom"
import {
  ShieldCheck, Database, Lock, GraduationCap, Cookie, Share2,
  UserCheck, Mail, ArrowRight, Eye, FileText,
} from "lucide-react"
import { SiteShell } from "../../components/site/SiteShell"
import { PageHero } from "../../components/site/PageHero"
import { Card, CardContent } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"
import { SCHOOL } from "../../lib/constants"

const sections = [
  {
    icon: Database,
    title: "1. Information We Collect",
    body: [
      "We collect information you provide directly to us when you create an account, apply for admission, register for courses, make payments, request transcripts or contact us. This includes your full name, email address, phone number, date of birth, gender, next-of-kin details, O-Level and JAMB results, passport photograph, medical information and academic records.",
      "We also collect information automatically when you use our website or Student Portal, including your IP address, browser type, device information, pages visited, time spent on pages and the referring URL. This data is collected through cookies and similar technologies, and is used to improve user experience and platform security.",
      "Payment information such as card numbers is processed directly by our payment partners (Paystack, Flutterwave, Monnify) and is never stored on our servers. We only retain transaction references, amounts and status for reconciliation and audit purposes.",
    ],
  },
  {
    icon: Eye,
    title: "2. How We Use Your Information",
    body: [
      "We use the information we collect to process your application for admission, register you for courses, generate examination results, issue certificates and transcripts, and manage your academic record throughout your time at the College.",
      "We also use your information to communicate important updates such as admission decisions, fee payment reminders, course registration deadlines, examination schedules and emergency announcements via email, SMS and in-portal notifications.",
      "Aggregated and de-identified data may be used for research, statistical analysis, institutional planning and reporting to regulatory bodies such as the National Commission for Colleges of Education (NCCE) and the Joint Admissions and Matriculation Board (JAMB).",
    ],
  },
  {
    icon: Lock,
    title: "3. Data Security",
    body: [
      "We implement industry-standard technical and organisational measures to protect your personal information from unauthorised access, alteration, disclosure or destruction. These include encryption in transit (TLS 1.2+), encryption at rest, role-based access control, audit logging and regular security reviews.",
      "Access to your personal information is restricted to authorised College staff who require it to perform their official duties. All staff are bound by confidentiality obligations and undergo periodic data protection training.",
      "Despite our efforts, no system can be guaranteed 100% secure. In the event of a data breach affecting your rights, we will notify you and the relevant authorities without undue delay, in accordance with the Nigeria Data Protection Act (NDPA) 2023 and the General Data Protection Regulation (GDPR) where applicable.",
    ],
  },
  {
    icon: GraduationCap,
    title: "4. Student Data Protection",
    body: [
      "Student records — including academic transcripts, results, certificates and ID cards — are confidential and protected by the College's records management policy. Access is limited to the student, authorised academic and administrative staff, and third parties to whom the student has expressly granted access in writing.",
      "Every official document issued by the College (result, transcript, certificate, ID card) carries a unique QR verification code that allows third parties to verify authenticity without disclosing additional student information. Verification tokens are time-limited and tied to a single document instance.",
      "Students may request access to their records at any time through the Student Portal. Requests for amendment of inaccurate records should be submitted to the Registrar with supporting evidence and will be processed within 30 working days.",
    ],
  },
  {
    icon: Cookie,
    title: "5. Cookies & Tracking Technologies",
    body: [
      "We use essential cookies to enable core functionality such as session authentication, theme preference and language selection. These cookies are necessary for the website to function and cannot be disabled in our systems.",
      "We also use analytical cookies to help us understand how visitors interact with the website so that we can improve performance and user experience. These cookies collect anonymous, aggregated information and do not identify individual users.",
      "You can control or disable non-essential cookies through your browser settings. However, doing so may impact certain features of the website. We do not use cookies for targeted advertising or sell cookie-derived data to third parties.",
    ],
  },
  {
    icon: Share2,
    title: "6. Third-Party Sharing",
    body: [
      "We share your information with trusted third-party service providers who help us deliver our services, including payment gateways (Paystack, Flutterwave, Monnify), email delivery (Resend), cloud hosting (Cloudflare) and QR verification infrastructure. These providers are contractually bound to protect your data and use it only for the purposes we specify.",
      "We may disclose your information when required by law, court order, or to comply with a valid request from a government or regulatory authority — including the NCCE, JAMB, the Kano State Ministry of Education and law enforcement agencies. We will only disclose the minimum information necessary to satisfy the request.",
      "We never sell your personal information to any third party for marketing or commercial purposes. We do not share your data with social media platforms for targeted advertising.",
    ],
  },
  {
    icon: UserCheck,
    title: "7. Your Rights",
    body: [
      "You have the right to access, correct, update or request deletion of your personal information held by the College. To exercise these rights, submit a written request to the Data Protection Officer at the Registrar's office or via email to privacy@ymscoe.edu.ng.",
      "You may also object to the processing of your personal information for direct marketing, request restriction of processing in certain circumstances, and receive a copy of your data in a structured, machine-readable format (data portability).",
      "Where we process your data based on your consent, you may withdraw that consent at any time by updating your account preferences or contacting us. Withdrawal of consent does not affect the lawfulness of processing carried out before the withdrawal.",
    ],
  },
  {
    icon: Mail,
    title: "8. Contact Us",
    body: [
      "If you have any questions, concerns or requests regarding this Privacy Policy or the handling of your personal information, please contact our Data Protection Officer using the details below. We aim to respond to all enquiries within 10 working days.",
      `You can reach the Data Protection Officer at ${SCHOOL.address}, or by email at privacy@ymscoe.edu.ng. For urgent matters, call ${SCHOOL.phone} during office hours (Monday to Friday, 8 AM to 5 PM WAT).`,
      "If you are not satisfied with our response, you have the right to lodge a complaint with the Nigeria Data Protection Commission (NDPC) at complaint.ndpc.gov.ng or the relevant supervisory authority in your jurisdiction.",
    ],
  },
]

export default function Privacy() {
  return (
    <SiteShell>
      <PageHero
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Privacy Policy" }]}
        subtitle="Legal"
        title="Privacy Policy"
        description="Your privacy matters to us. This policy explains what personal information Yusuf Maitama Sule College of Education collects, how we use it, the security measures we apply and the rights you have over your data."
      />

      {/* Intro card */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Card className="border-0 shadow-md bg-gradient-to-br from-brand-50 to-gold-50 dark:from-brand-900/20 dark:to-gold-900/20">
            <CardContent className="pt-6 flex flex-col sm:flex-row items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-brand-700 grid place-items-center shrink-0">
                <ShieldCheck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-1">Effective Date: January 1, 2025</h3>
                <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed">
                  This Privacy Policy is made in accordance with the Nigeria Data Protection Act (NDPA) 2023 and applies to all users of the Yusuf Maitama Sule College of Education website, Student Portal, Admin Portal and related services.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Sections */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">
            {sections.map(s => (
              <Card key={s.title} className="border-0 shadow-md">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-900/40 grid place-items-center shrink-0">
                      <s.icon className="w-5 h-5 text-brand-700 dark:text-brand-400" />
                    </div>
                    <h2 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 leading-tight pt-1">{s.title}</h2>
                  </div>
                  <div className="space-y-3">
                    {s.body.map((para, i) => (
                      <p key={i} className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed">{para}</p>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer note */}
      <section className="py-16 bg-brand-950 dark:bg-ink-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-20" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <FileText className="w-12 h-12 text-gold-300 mx-auto mb-4" />
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">Questions About Your Privacy?</h2>
            <p className="text-white/75 mb-6">
              Our Data Protection Officer is here to help with any enquiry about how we collect, use or safeguard your personal information.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/contact">
                <Button size="lg" className="bg-gold-500 hover:bg-gold-600 text-ink-950">
                  Contact DPO <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/terms">
                <Button size="lg" variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white/10">
                  View Terms of Service
                </Button>
              </Link>
            </div>
            <div className="mt-8 text-xs text-white/50">
              <Badge variant="outline" className="border-white/20 text-white/70">Last reviewed: January 2025</Badge>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  )
}
